import { isWcmEditor } from "../../../../../config/serverConfig";
import { RylzDisplayItem } from "./rylzDisplay";

interface RylzDesktopItemProps {
    item: RylzDisplayItem
    itemIndex: number
    currentPromoIndex: number
    isPromoCarouselInView: boolean
    showVideoDuration?: boolean
    backgroundColorLabel?: string
    imageAutoAdvanceSeconds?: number
    onItemEnded: () => void
    onOpenFeed: (index: number) => void
    onPromoMouseHover: (hoveredIndex: number | null, videoRef?: HTMLVideoElement) => void
}

interface RylzDesktopItemState {
    isMouseHover: boolean
}

export class RylzDesktopItem extends React.Component<RylzDesktopItemProps, RylzDesktopItemState> {
    constructor(props) {
        super(props);
        this.state = {
            isMouseHover: false,
        }
    }

    private videoRef: HTMLVideoElement | null = null;
    private imageAdvanceTimer: number | null = null;

    componentDidUpdate(prevProps: RylzDesktopItemProps) {
        const { itemIndex, currentPromoIndex, item, isPromoCarouselInView, imageAutoAdvanceSeconds } = this.props;

        const becameActive = currentPromoIndex === itemIndex && prevProps.currentPromoIndex !== itemIndex;
        const leftActive = prevProps.currentPromoIndex === itemIndex && currentPromoIndex !== itemIndex;
        if (becameActive && this.videoRef && item.promoPath && isPromoCarouselInView) {
            const playPromise = this.videoRef.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => { /* autoplay can be blocked; muted+autoPlay is the fallback */ });
            }
        }
        if (becameActive || leftActive || prevProps.isPromoCarouselInView !== isPromoCarouselInView || prevProps.imageAutoAdvanceSeconds !== imageAutoAdvanceSeconds) {
            this.syncImageAdvanceTimer();
        }
    }

    componentDidMount() {
        this.syncImageAdvanceTimer();
    }

    componentWillUnmount() {
        this.clearImageAdvanceTimer();
    }

    private clearImageAdvanceTimer = () => {
        if (this.imageAdvanceTimer != null) {
            window.clearTimeout(this.imageAdvanceTimer);
            this.imageAdvanceTimer = null;
        }
    }

    private syncImageAdvanceTimer = () => {
        this.clearImageAdvanceTimer();
        const { item, itemIndex, currentPromoIndex, isPromoCarouselInView, imageAutoAdvanceSeconds, onItemEnded } = this.props;
        const isImage = !item.isVideo;
        const seconds = imageAutoAdvanceSeconds || 0;
        if (isWcmEditor() || !isPromoCarouselInView || currentPromoIndex !== itemIndex || !isImage || seconds <= 0) return;

        this.imageAdvanceTimer = window.setTimeout(() => {
            onItemEnded();
        }, seconds * 1000);
    }

    private onMouseHover = () => {
        const { itemIndex, onPromoMouseHover, item } = this.props;
        this.setState({ isMouseHover: true });
        onPromoMouseHover(itemIndex);
        if (this.videoRef && item.promoPath) this.videoRef.play();
    }

    private onMouseLeave = () => {
        const { onPromoMouseHover } = this.props;
        this.setState({ isMouseHover: false });
        onPromoMouseHover(null, this.videoRef || undefined);
    }

    private secondsToMinutesHandler = (seconds: number) => {
        const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
        const newSeconds = (seconds % 60).toString().padStart(2, "0");
        return `${minutes}:${newSeconds}`;
    }

    private onVideoError = () => {
        if (!this.state.isMouseHover) {
            this.props.onItemEnded();
        }
    }

    private isWhiteLabelBackground = (color?: string) => {
        if (!color) return true;
        const normalized = color.trim().toLowerCase();
        return normalized === "#fff" || normalized === "#ffffff" || normalized === "white";
    }

    private renderLabel = () => {
        const { item, backgroundColorLabel } = this.props;
        const labelData = item.labelData;
        if (!labelData) return null;

        if (labelData.author) {
            return (
                <div className="commentary-label">
                    <span className="author-image-wrapper">
                        <img className="author-image" src={labelData.logoUrl} alt={labelData.author} />
                    </span>
                    <div className="commentary-info">
                        <div className="title">פרשנות</div>
                        <div className="author">{labelData.author}</div>
                    </div>
                </div>
            );
        }

        if (labelData.logoUrl) {
            return <img className="logo-image" src={labelData.logoUrl} alt={labelData.name || ""} />;
        }

        if (labelData.name) {
            const isWhite = this.isWhiteLabelBackground(backgroundColorLabel);
            const labelStyle: React.CSSProperties = {
                backgroundColor: backgroundColorLabel || "#FFFFFF",
                color: isWhite ? undefined : "#E1E1E1",
            };
            return (
                <div
                    className={`label-text ${isWhite ? "label-text-default" : "label-text-custom"}`}
                    style={labelStyle}
                    dangerouslySetInnerHTML={{ __html: labelData.name }}
                />
            );
        }

        return null;
    }

    public render() {
        const { isMouseHover } = this.state;
        const { item, itemIndex, currentPromoIndex, isPromoCarouselInView, onOpenFeed, onItemEnded, showVideoDuration } = this.props;
        const isActiveSlot = !isWcmEditor() && isPromoCarouselInView && currentPromoIndex === itemIndex;
        const shouldDisplayPromo = isActiveSlot && !!item.promoPath;
        const slotClasses = `slotView ${isActiveSlot ? "promo-playing" : ""} ${isMouseHover ? "is-hovered" : ""}`;

        return (
            <div className={slotClasses} id={`rylzDesktopItem_${itemIndex}`} onClick={() => onOpenFeed(itemIndex)} onMouseEnter={this.onMouseHover} onMouseLeave={this.onMouseLeave}>
                <div className="media-container">
                    {shouldDisplayPromo &&
                        <video
                            muted
                            autoPlay={true}
                            loop={isMouseHover}
                            src={item.promoPath}
                            onEnded={onItemEnded}
                            className="promoVideo"
                            poster={item.posterSrc}
                            onError={this.onVideoError}
                            ref={ref => this.videoRef = ref}
                        />
                    }
                    {item.posterSrc && <img src={item.posterSrc} alt={item.title || ""} />}
                </div>

                <div className="contentDiv">
                    {item.labelData && <div className="item-label">{this.renderLabel()}</div>}
                    {item.title && <div className="item-title" dangerouslySetInnerHTML={{ __html: item.title }} />}
                    {showVideoDuration && item.isVideo && item.duration != null && item.duration > 0 &&
                        <div className="item-video-duration">
                            <span className="duration-text">{this.secondsToMinutesHandler(Math.round(item.duration))}</span>
                            <span className="duration-play-icon"></span>
                        </div>
                    }
                </div>
            </div>
        )
    }
}
